import { toJS } from 'mobx'

/**
 * Дії над фільтром колекції, спільні для смуги фільтра і чіпів:
 * застосувати значення форми, зняти один ключ, скинути все.
 * Повторює поведінку Core Filter.jsx (setFilter + load, reset single-filter форм).
 */

/** Типи полів, для яких застосування фільтра відкладається (debounce), бо користувач набирає текст */
export const DEBOUNCED_FIELD_TYPES = ['string', 'text', 'textarea', 'number', 'email', 'phone', 'mask']

/** Пауза після останнього символу перед застосуванням текстового фільтра */
export const FILTER_DEBOUNCE_MS = 400

export interface FilterActions {
    /** Застосувати поточні значення форми фільтра (з першої сторінки) */
    apply: () => void
    /** Зняти один фільтр і одразу застосувати */
    removeKey: (key: string) => void
    /** Скинути всі фільтри і перезавантажити список */
    clear: () => void
}

/**
 * @param crudContext контекст CRUD ресурсу
 * @param withSuppressed виконує колбек так, щоб реакція смуги фільтра на зміну значень
 *   не спрацьовувала (бо завантаження робимо самі); за замовчуванням просто викликає колбек
 */
export const createFilterActions = (
    crudContext: any,
    withSuppressed: (fn: () => void) => void = (fn) => fn()
): FilterActions => {
    const apply = () => {
        const listContext = crudContext.listContext
        const form = crudContext.filterFormContext
        if (!listContext || !form) return

        listContext.setFilter({ ...toJS(listContext.filter), ...toJS(form.values) })
        listContext.setCurrentPage(1)
        listContext.actions.load()
    }

    const removeKey = (key: string) => {
        const form = crudContext.filterFormContext
        const listContext = crudContext.listContext
        if (!form) return

        withSuppressed(() => {
            // Ключ прибираємо і з фільтра списку: маркерні поля (напр. «без водія» з переходу
            // з головної) не мають поля у формі, тож apply() інакше повернув би їх назад
            if (listContext) {
                const next = { ...toJS(listContext.filter) }
                delete next[key]
                delete next[`before_${key}`]
                listContext.setFilter(next)
            }
            form.setValue(key, undefined)
            // Службовий оператор числового діапазону (див. Core Filter) знімаємо разом із значенням
            if (form.getField(`before_${key}`)) form.setValue(`before_${key}`, undefined)
        })
        apply()
    }

    const clear = () => {
        const listContext = crudContext.listContext
        const form = crudContext.filterFormContext
        if (!listContext || !form) return

        withSuppressed(() => {
            listContext.setFilter({})
            form.reset()

            const singleForms = crudContext.storages?.singleFilterForms
            if (singleForms && typeof singleForms === 'object') {
                Object.keys(singleForms).forEach((name) => singleForms[name]?.setInitialValues?.({}))
            }
        })

        listContext.setCurrentPage(1)
        listContext.actions.load(true)
    }

    return { apply, removeKey, clear }
}
